// Guided-tour steps, keyed by the same route paths as helpContent.js.
//
// Each step is { target, title, body, placement }:
//   target    — CSS selector for the element to spotlight. Omit (or use null)
//               for a centred step that isn't anchored to anything.
//   placement — preferred side: 'top' | 'bottom' | 'left' | 'right'. The tour
//               flips it automatically when there isn't room.
//
// A step whose target isn't on the page is skipped rather than shown against
// empty space, so tours stay valid when a control is role-gated or a list is
// empty.

// Shown once, on first login, regardless of which page the user lands on.
export const welcomeTour = {
  id: "welcome",
  title: "Welcome to Post-Call Analytics",
  steps: [
    {
      target: null,
      title: "Welcome aboard",
      body: "This is a quick tour of the essentials. It takes about a minute, and you can leave at any point — every page also has its own tour you can start from the help button."
    },
    {
      target: "aside nav",
      placement: "right",
      title: "Finding your way",
      body: "The sidebar groups everything by what you're doing: Overview for reporting, Quality for reviewing calls, Operations for getting recordings in, and Administration for settings."
    },
    {
      target: "[data-tour='global-search']",
      placement: "bottom",
      title: "Search from anywhere",
      body: "Look up a call, agent, or campaign from any page. Press Enter to open the full search with all the filters."
    },
    {
      target: "[data-tour='help-button']",
      placement: "bottom",
      title: "Help, whenever you need it",
      body: "This opens help for whichever page you're on, and lets you replay that page's tour. Nothing here is a one-time-only thing."
    },
    {
      target: "[data-tour='user-menu']",
      placement: "bottom",
      title: "Your account",
      body: "Switch between light and dark mode, and sign out, from here."
    }
  ]
};

export const tourContent = {
  "/": {
    id: "campaign-home",
    title: "Campaign Home",
    steps: [
      {
        target: null,
        title: "Campaign Home",
        body: "Everything that has been processed lands here. This tour points out the filters, the summary figures, and how to send calls to a reviewer."
      },
      {
        target: "[data-testid='campaign-filter']",
        placement: "right",
        title: "Narrow the list",
        body: "Start by picking a campaign. The date range, compliance priority, and scorecard filters below it all narrow the table further."
      },
      {
        target: "[data-tour='results-chart']",
        placement: "left",
        title: "Pass, medium, fail at a glance",
        body: "The distribution of outcomes. Click any bar to filter the table to just those calls."
      },
      {
        target: "[data-testid='campaign-pass-rate']",
        placement: "bottom",
        title: "Headline figures",
        body: "Volume, pass rate, and failures for whatever your filters currently select — these update as you change the filters above."
      },
      {
        target: "[data-tour='calls-table']",
        placement: "top",
        title: "The calls themselves",
        body: "Click a call name to open its transcript and scorecard. Tick the boxes on the left to select several at once."
      },
      {
        target: "[data-tour='calls-table']",
        placement: "top",
        title: "Assigning for review",
        body: "With calls selected, an \"Assign QA Agent\" button appears above the table so you can hand them to a reviewer."
      }
    ]
  },

  "/search": {
    id: "search",
    title: "Search",
    steps: [
      {
        target: null,
        title: "Multi-layered search",
        body: "Three ways to find calls, depending on how specific you need to be."
      },
      {
        target: "[data-tour='search-tabs']",
        placement: "bottom",
        title: "Pick your depth",
        body: "Quick Filters are one-click shortcuts for common questions. Basic Search covers agent, campaign, date and score. Advanced adds job, assignee, GUID, and duration."
      },
      {
        target: "[data-tour='search-panel']",
        placement: "top",
        title: "Combining filters",
        body: "Multiple agents or score ranges match any of them; every other filter narrows further. The note under the heading explains which filters run against the index and which refine afterwards."
      }
    ]
  },

  "/uploadRecs": {
    id: "upload",
    title: "Upload Recordings",
    steps: [
      {
        target: null,
        title: "Getting recordings in",
        body: "Two routes: upload audio files directly, or pull them from Genesys."
      },
      {
        target: "[data-tour='upload-tabs']",
        placement: "bottom",
        title: "Choose a source",
        body: "File Upload for audio you already have. The Genesys tabs fetch recordings by conversation, either in bulk or one at a time."
      },
      {
        target: "[data-tour='upload-campaign']",
        placement: "right",
        title: "Set these first",
        body: "The campaign and scorecard decide how the calls get graded, so choose them before adding files — they can't be changed once processing starts."
      },
      {
        target: "[data-tour='upload-dropzone']",
        placement: "top",
        title: "Add your files",
        body: "Drag audio in or browse for it. Five or more files at once are processed as a batch, which you can then follow on the Jobs page."
      }
    ]
  },

  "/templates": {
    id: "scorecards",
    title: "Scorecards",
    steps: [
      {
        target: null,
        title: "Scorecards",
        body: "A scorecard is the set of questions a call gets graded against. Campaigns reference them, so editing one affects every campaign using it."
      },
      {
        target: "[data-tour='scorecard-actions']",
        placement: "bottom",
        title: "Creating one",
        body: "Build a scorecard from scratch, or upload an existing one from a PDF, spreadsheet, or document and let it be extracted for you."
      }
    ]
  },

  "/templates/edit": {
    id: "scorecard-editor",
    title: "Edit Scorecard",
    steps: [
      {
        target: null,
        title: "Editing a scorecard",
        body: "This is where the questions live. The most important thing to get right is how you word conditional questions — the next steps cover that."
      },
      {
        target: "[data-tour='scoring-system']",
        placement: "bottom",
        title: "Scoring mode",
        body: "Yes/No scoring gives a question its full weight when answered Yes. Numeric scoring grades each question from 0 to 100 instead."
      },
      {
        target: null,
        title: "Questions that don't always apply",
        body: "Mark a Conduct question \"not always applicable\" and add pre-check steps that decide whether it applies to a given call. Answering No records it as Not Applicable, which is not a failure."
      },
      {
        target: null,
        title: "Write the situation, not the rule",
        body: "Describe when the question APPLIES — \"an NRV bundle is referred to anywhere in the transcript\". Don't paste a marking instruction like \"no NRV mentioned = mark N/A\": that inverts the check and skips the question on exactly the calls it should have caught."
      }
    ]
  },

  "/campaigns": {
    id: "campaigns",
    title: "Campaigns",
    steps: [
      {
        target: null,
        title: "Campaigns",
        body: "A campaign groups recordings that share a scorecard and configuration. Every uploaded call belongs to one."
      },
      {
        target: "[data-tour='campaign-table']",
        placement: "top",
        title: "Managing them",
        body: "View shows a campaign's scorecards, Edit changes them, and Deactivate retires a campaign without deleting its history."
      }
    ]
  },

  "/jobs": {
    id: "jobs",
    title: "Jobs",
    steps: [
      {
        target: null,
        title: "Upload jobs",
        body: "Every batch upload appears here so you can follow it through processing."
      },
      {
        target: "[data-tour='job-stats']",
        placement: "bottom",
        title: "Where things stand",
        body: "How many calls are assessed, still awaiting assessment, or failed across all your jobs."
      },
      {
        target: "[data-tour='jobs-table']",
        placement: "top",
        title: "Per-batch detail",
        body: "Open a job to see its individual calls, stop an in-progress Genesys fetch, or export the results."
      }
    ]
  },

  "/dashboard": {
    id: "call-detail",
    title: "Call Details",
    steps: [
      {
        target: null,
        title: "A single call",
        body: "Everything known about one recording: the AI summary, the transcript with audio, and the QA scorecard."
      },
      {
        target: "[data-tour='section-nav']",
        placement: "bottom",
        title: "Jumping between sections",
        body: "Skip straight to Overview, Transcript, or QA. Each section also has a Hide control so you can fold away the parts you're not using."
      },
      {
        target: "#transcript",
        placement: "top",
        title: "Transcript and audio",
        body: "The agent's turns sit on the right, the customer's on the left. Click any timestamp to jump the audio to that moment."
      },
      {
        target: "#qa",
        placement: "top",
        title: "The assessment",
        body: "Review the scores, use Override to correct one, then Mark as Assessed to lock the call. Overrides are recorded in the Audit log."
      }
    ]
  },

  "/assigned": {
    id: "assigned",
    title: "Assigned to Me",
    steps: [
      {
        target: null,
        title: "Your review queue",
        body: "Calls that have been assigned to you for scoring. Open one to work through its scorecard, then mark it assessed to clear it from the queue."
      }
    ]
  },

  "/CallAgent": {
    id: "my-calls",
    title: "My Calls",
    steps: [
      {
        target: null,
        title: "Your calls",
        body: "Your own calls that have been through QA. Open one to read the feedback and see how each question was scored."
      }
    ]
  },

  "/scheduled-jobs": {
    id: "scheduled-jobs",
    title: "Scheduled Jobs",
    steps: [
      {
        target: null,
        title: "Automating the fetch",
        body: "Instead of uploading by hand, schedule a recurring pull of new recordings."
      },
      {
        target: "[data-tour='schedule-form']",
        placement: "top",
        title: "Setting one up",
        body: "Work down the numbered steps: name it and pick the campaign and scorecard, choose how often it runs, then set how far back each run should look. Keep the extraction window matched to the run frequency so you don't fetch the same calls twice or miss any."
      }
    ]
  },

  "/reports": {
    id: "reports",
    title: "Reports",
    steps: [
      {
        target: null,
        title: "QA reports",
        body: "Generate a per-call, per-question export for a date range — useful for sharing results outside the tool."
      },
      {
        target: "[data-tour='report-filters']",
        placement: "bottom",
        title: "Scope the report",
        body: "Start from a preset range, then narrow by campaign, scorecard, agent, or a specific call before generating. Choose whether days count by call date or processed date (SAST)."
      }
    ]
  },

  "/users": {
    id: "users",
    title: "User Management",
    steps: [
      {
        target: null,
        title: "Users and access",
        body: "Who can sign in and what they're allowed to do."
      },
      {
        target: "[data-tour='user-tabs']",
        placement: "bottom",
        title: "Three views",
        body: "Users manages accounts and roles. Agent Emails maps agent names to email addresses so reports reach the right person. Activity Log shows what an administrator has changed."
      }
    ]
  },

  "/override-logs": {
    id: "audit",
    title: "Audit",
    steps: [
      {
        target: null,
        title: "Audit trail",
        body: "Every manual override of an AI score, with who changed it and when."
      },
      {
        target: "[data-tour='audit-search']",
        placement: "bottom",
        title: "Finding a change",
        body: "Query an exact filename to trace one recording, or filter the loaded logs by reviewer, campaign, or scorecard. Open an entry to see the old and new values side by side."
      }
    ]
  },

  "/fileCopy": {
    id: "cloud-connector",
    title: "Cloud Connector",
    steps: [
      {
        target: null,
        title: "Pulling from external storage",
        body: "Copy recordings in from an S3 bucket or FTP server that lives outside this solution."
      },
      {
        target: "[data-tour='connector-source']",
        placement: "bottom",
        title: "Connect, then copy",
        body: "Pick the source type and enter its details, use Test Connection to check access, then select the files you want and start the copy. Copied files are processed like any other upload."
      }
    ]
  },

  "/transcribe-quota": {
    id: "transcribe-quota",
    title: "Call Limiter",
    steps: [
      {
        target: null,
        title: "Daily transcription limit",
        body: "Caps how many calls are transcribed per day. Calls beyond the limit are paused rather than dropped."
      },
      {
        target: "[data-tour='quota-actions']",
        placement: "bottom",
        title: "Limits and paused work",
        body: "Override today's limit, or set tomorrow's default. Paused jobs listed below can be resumed once there's capacity, or denied if they're no longer wanted."
      }
    ]
  }
};

// Resolves a pathname to its tour, mirroring getHelpForPath's dynamic-route
// handling so both features agree on what page you're looking at.
export function getTourForPath(pathname) {
  if (tourContent[pathname]) return tourContent[pathname];

  if (pathname === "/dashboard/parsedFiles/search") return tourContent["/search"];
  if (pathname.startsWith("/dashboard/")) return tourContent["/dashboard"];
  if (pathname.startsWith("/jobs/")) return tourContent["/jobs"];
  if (
    pathname.startsWith("/templates/") &&
    pathname !== "/templates/new" &&
    pathname !== "/templates/upload"
  ) {
    return tourContent["/templates/edit"];
  }
  return null;
}
